/**
 * CapabilityListView — assessment catalog + share/distribute + reminder-tracker
 * dialog. Pure presentational component; all state and side-effects live in
 * the parent (CapabilityMetricsPage).
 */
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  ClipboardCheck, ChevronRight, Loader2, BarChart3, Target, Mail, Bell, Clock,
  AlertTriangle, CalendarDays, UserCheck, UserX, CheckCircle, TrendingUp,
} from 'lucide-react';
import { format } from 'date-fns';
import { ShareEmailDialog } from './ShareEmailDialog';

export const CapabilityListView = ({
  // Data
  loading,
  assessments,
  submissions,
  analyticsLoading,
  shareDialogOpen,
  shareType,
  sharing,
  reminderDialogOpen,
  reminderLoading,
  completionStatus,
  reminderDeadline,
  reminderMessage,
  reminderConfig,
  sendingReminder,
  // Setters
  setView,
  setShareDialogOpen,
  setReminderDialogOpen,
  setReminderDeadline,
  setReminderMessage,
  // Handlers
  onFetchAnalytics,
  onStartAssessment,
  onShareAssessment,
  onOpenReminderTracker,
  onShareSend,
  onSaveReminderConfig,
  onSendReminders,
}) => {
  return (
    <div data-testid="capability-assessment-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">Capability Assessment</h1>
          <p className="text-[#4B5563] text-sm">Evaluate your organization's knowledge management maturity</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setView('benchmarking')} data-testid="benchmarking-btn">
            <Target className="w-4 h-4 mr-1.5" />KM Benchmarking
          </Button>
          <Button variant="outline" size="sm" onClick={onFetchAnalytics} disabled={analyticsLoading} data-testid="team-analytics-btn">
            {analyticsLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <TrendingUp className="w-4 h-4 mr-1.5" />}Team Analytics
          </Button>
          {submissions.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => setView('history')} data-testid="view-history-btn">
              <BarChart3 className="w-4 h-4 mr-1.5" />My Results ({submissions.length})
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" /></div>
      ) : assessments.length === 0 ? (
        <Card className="border-[#E5E7EB]"><CardContent className="p-12 text-center">
          <ClipboardCheck className="w-12 h-12 text-[#E5E7EB] mx-auto mb-4" />
          <p className="text-[#9CA3AF]">No assessments available yet.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          {assessments.map((a) => (
            <Card key={a.assessment_id} className="border-[#E5E7EB] hover:border-[#002FA7] transition-colors" data-testid={`assessment-${a.assessment_id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#002FA7]/10 flex items-center justify-center flex-shrink-0">
                      <ClipboardCheck className="w-6 h-6 text-[#002FA7]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-[#0A0A0A]">{a.title}</h3>
                      <p className="text-sm text-[#4B5563] mt-1">{a.description}</p>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {a.sections?.map((s) => <Badge key={s.section_id} variant="secondary" className="text-xs">{s.title} ({s.questions.length}q)</Badge>)}
                      </div>
                      <p className="text-xs text-[#9CA3AF] mt-2">{a.sections?.reduce((sum, s) => sum + s.questions.length, 0)} total questions across {a.sections?.length} sections</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <Button onClick={() => onStartAssessment(a)} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="start-assessment-btn">
                      Start Assessment<ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs text-[#002FA7] border-[#002FA7]/30" onClick={() => onShareAssessment(a)} data-testid="share-assessment-btn">
                      <Mail className="w-3.5 h-3.5 mr-1" />Distribute via Email
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs text-[#FF9500] border-[#FF9500]/30" onClick={() => onOpenReminderTracker(a)} data-testid="reminder-tracker-btn">
                      <Bell className="w-3.5 h-3.5 mr-1" />Reminders & Tracker
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ShareEmailDialog
        open={shareDialogOpen}
        onClose={setShareDialogOpen}
        title={shareType === 'assessment' ? 'Distribute Assessment via Email' : 'Share Results via Email'}
        onSend={onShareSend}
        sending={sharing}
      />

      {/* Reminder & Tracker Dialog */}
      <Dialog open={reminderDialogOpen} onOpenChange={setReminderDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="reminder-tracker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Bell className="w-5 h-5 text-[#FF9500]" />Reminders & Completion Tracker</DialogTitle>
          </DialogHeader>
          {reminderLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" /></div>
          ) : (
            <div className="space-y-4">
              {/* Completion Stats */}
              {completionStatus && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="completion-stats">
                  <div className="p-3 rounded-lg bg-[#002FA7]/5 text-center">
                    <p className="text-xl font-bold text-[#002FA7]">{completionStatus.total_invited}</p>
                    <p className="text-[9px] text-[#9CA3AF]">Invited</p>
                  </div>
                  <div className="p-3 rounded-lg bg-[#34C759]/5 text-center">
                    <p className="text-xl font-bold text-[#34C759]">{completionStatus.total_completed}</p>
                    <p className="text-[9px] text-[#9CA3AF]">Completed</p>
                  </div>
                  <div className="p-3 rounded-lg bg-[#FF9500]/5 text-center">
                    <p className="text-xl font-bold text-[#FF9500]">{completionStatus.total_pending}</p>
                    <p className="text-[9px] text-[#9CA3AF]">Pending</p>
                  </div>
                  <div className="p-3 rounded-lg bg-[#FF3B30]/5 text-center">
                    <p className="text-xl font-bold text-[#FF3B30]">{completionStatus.total_overdue}</p>
                    <p className="text-[9px] text-[#9CA3AF]">Overdue</p>
                  </div>
                </div>
              )}

              {/* Completion Rate Bar */}
              {completionStatus && completionStatus.total_invited > 0 && (
                <div data-testid="completion-rate-bar">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-[#4B5563]">Completion Rate</span>
                    <span className="text-xs font-bold" style={{ color: completionStatus.completion_rate >= 80 ? '#34C759' : completionStatus.completion_rate >= 50 ? '#FF9500' : '#FF3B30' }}>{completionStatus.completion_rate}%</span>
                  </div>
                  <Progress value={completionStatus.completion_rate} className="h-2" />
                </div>
              )}

              {/* Reminder Config */}
              <Card className="border-[#E5E7EB]">
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><CalendarDays className="w-4 h-4 text-[#FF9500]" />Reminder Settings</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-xs">Deadline</Label>
                    <Input type="date" value={reminderDeadline} onChange={e => setReminderDeadline(e.target.value)} className="text-sm mt-1" data-testid="reminder-deadline-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Reminder Message (included in reminder emails)</Label>
                    <Textarea rows={2} value={reminderMessage} onChange={e => setReminderMessage(e.target.value)} placeholder="Please complete the assessment before the deadline..." className="text-sm mt-1" data-testid="reminder-message-input" />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={onSaveReminderConfig} data-testid="save-reminder-config-btn">
                      <CheckCircle className="w-3.5 h-3.5 mr-1" />Save Settings
                    </Button>
                    {reminderConfig?.last_reminder_at && (
                      <p className="text-[10px] text-[#9CA3AF] self-center">Last sent: {format(new Date(reminderConfig.last_reminder_at), 'MMM d, yyyy h:mm a')} ({reminderConfig.reminder_count} total)</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* People Lists */}
              {completionStatus && (
                <div className="space-y-3">
                  {/* Overdue */}
                  {completionStatus.overdue.length > 0 && (
                    <Card className="border-[#FF3B30]/20" data-testid="overdue-list">
                      <CardHeader className="pb-1"><CardTitle className="text-xs flex items-center gap-1.5 text-[#FF3B30]"><AlertTriangle className="w-3.5 h-3.5" />Overdue ({completionStatus.overdue.length})</CardTitle></CardHeader>
                      <CardContent className="p-2">
                        {completionStatus.overdue.map((p, i) => (
                          <div key={i} className="flex items-center gap-2 px-2 py-1.5 text-xs text-[#4B5563]">
                            <UserX className="w-3 h-3 text-[#FF3B30]" /><span>{p.name}</span><span className="text-[#9CA3AF] text-[10px]">{p.email}</span>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}

                  {/* Pending */}
                  {completionStatus.pending.length > 0 && (
                    <Card className="border-[#FF9500]/20" data-testid="pending-list">
                      <CardHeader className="pb-1"><CardTitle className="text-xs flex items-center gap-1.5 text-[#FF9500]"><Clock className="w-3.5 h-3.5" />Pending ({completionStatus.pending.length})</CardTitle></CardHeader>
                      <CardContent className="p-2">
                        {completionStatus.pending.map((p, i) => (
                          <div key={i} className="flex items-center gap-2 px-2 py-1.5 text-xs text-[#4B5563]">
                            <Clock className="w-3 h-3 text-[#FF9500]" /><span>{p.name}</span><span className="text-[#9CA3AF] text-[10px]">{p.email}</span>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}

                  {/* Completed */}
                  {completionStatus.completed.length > 0 && (
                    <Card className="border-[#34C759]/20" data-testid="completed-list">
                      <CardHeader className="pb-1"><CardTitle className="text-xs flex items-center gap-1.5 text-[#34C759]"><CheckCircle className="w-3.5 h-3.5" />Completed ({completionStatus.completed.length})</CardTitle></CardHeader>
                      <CardContent className="p-2">
                        {completionStatus.completed.map((p, i) => (
                          <div key={i} className="flex items-center justify-between px-2 py-1.5 text-xs">
                            <div className="flex items-center gap-2 text-[#4B5563]">
                              <UserCheck className="w-3 h-3 text-[#34C759]" /><span>{p.user_name}</span><span className="text-[#9CA3AF] text-[10px]">{p.email}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className="text-[8px]" style={{ backgroundColor: p.maturity_level === 'Advanced' ? '#34C75915' : p.maturity_level === 'Intermediate' ? '#007AFF15' : '#FF950015', color: p.maturity_level === 'Advanced' ? '#34C759' : p.maturity_level === 'Intermediate' ? '#007AFF' : '#FF9500' }}>{p.maturity_level}</Badge>
                              <span className="font-bold text-[#002FA7]">{p.overall_score}%</span>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}

                  {completionStatus.total_invited === 0 && (
                    <div className="text-center py-6 text-[#9CA3AF] text-sm">
                      <Mail className="w-8 h-8 mx-auto mb-2 text-[#E5E7EB]" />
                      <p>No one has been invited yet. Use "Distribute via Email" to invite people first.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Send Reminders Button */}
              {completionStatus && (completionStatus.total_pending > 0 || completionStatus.total_overdue > 0) && (
                <Button className="w-full bg-[#FF9500] hover:bg-[#E68A00]" onClick={onSendReminders} disabled={sendingReminder} data-testid="send-reminders-btn">
                  {sendingReminder ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
                  Send Reminders to {completionStatus.total_pending + completionStatus.total_overdue} Non-Completer(s)
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
